import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { rupiah, tgl, today } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const KATEGORI = ["HP", "Laptop", "Tablet", "Kamera", "TV & Audio", "Lainnya"];
const KONDISI = ["Seperti Baru", "Sangat Baik", "Baik", "Cacat Ringan"];

const empty = { nama: "", kategori: "HP", kondisi: "Baik", harga_beli: "", harga_jual_estimasi: "", penjual: "", metode_beli: "tunai", tanggal_beli: today(), catatan: "" };

export default function Pembelian() {
  const [form, setForm] = useState(empty);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => api.get("/items").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/items", {
        ...form,
        harga_beli: Number(form.harga_beli),
        harga_jual_estimasi: Number(form.harga_jual_estimasi),
      });
      toast.success("Barang masuk ke stok");
      setForm(empty);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan pembelian"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="pembelian-page">
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Barang Masuk</p>
      <h1 className="mt-1 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>Pembelian Barang</h1>

      <form onSubmit={submit} className="mt-5 rounded-md border border-border bg-white p-5 space-y-4" data-testid="pembelian-form">
        <div className="space-y-1.5">
          <Label>Nama Barang</Label>
          <Input required value={form.nama} onChange={(e) => set("nama", e.target.value)} placeholder="cth: iPhone 11 64GB" data-testid="pembelian-nama-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Kategori</Label>
            <Select value={form.kategori} onValueChange={(v) => set("kategori", v)}>
              <SelectTrigger data-testid="pembelian-kategori-select"><SelectValue /></SelectTrigger>
              <SelectContent>{KATEGORI.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Kondisi</Label>
            <Select value={form.kondisi} onValueChange={(v) => set("kondisi", v)}>
              <SelectTrigger data-testid="pembelian-kondisi-select"><SelectValue /></SelectTrigger>
              <SelectContent>{KONDISI.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Harga Beli (Rp)</Label>
            <Input required type="number" min="0" value={form.harga_beli} onChange={(e) => set("harga_beli", e.target.value)} placeholder="1500000" data-testid="pembelian-harga-beli-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Harga Jual Direncanakan (Rp)</Label>
            <Input required type="number" min="0" value={form.harga_jual_estimasi} onChange={(e) => set("harga_jual_estimasi", e.target.value)} placeholder="cth: 3200000" data-testid="pembelian-harga-jual-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Beli Dari (Penjual)</Label>
            <Input value={form.penjual} onChange={(e) => set("penjual", e.target.value)} placeholder="Nama penjual" data-testid="pembelian-penjual-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Tanggal Beli</Label>
            <Input type="date" value={form.tanggal_beli} onChange={(e) => set("tanggal_beli", e.target.value)} data-testid="pembelian-tanggal-input" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Pembayaran</Label>
          <div className="grid grid-cols-2 gap-2">
            {[["tunai", "Tunai (Kas)"], ["transfer", "Transfer Bank"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => set("metode_beli", v)} data-testid={`pembelian-metode-${v}`}
                className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors duration-150 active:scale-[0.98] ${form.metode_beli === v ? "border-primary bg-primary text-white" : "border-border bg-white"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Catatan</Label>
          <Input value={form.catatan} onChange={(e) => set("catatan", e.target.value)} placeholder="Opsional: kelengkapan, minus, dll" data-testid="pembelian-catatan-input" />
        </div>
        <Button type="submit" className="w-full rounded-full active:scale-95 transition-transform duration-100" disabled={loading} data-testid="pembelian-submit-button">
          {loading ? "Menyimpan..." : "Simpan ke Stok"}
        </Button>
      </form>

      <h2 className="mt-8 text-base font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>Riwayat Pembelian</h2>
      <div className="mt-3 rounded-md border border-border bg-white divide-y divide-border" data-testid="pembelian-list">
        {items.length === 0 && <p className="p-4 text-sm text-muted-foreground">Belum ada barang masuk.</p>}
        {items.map((i) => (
          <div key={i.id} className="flex items-center justify-between p-3.5" data-testid={`pembelian-item-${i.id}`}>
            <div>
              <p className="text-sm font-semibold">{i.nama} <span className="text-xs font-normal text-muted-foreground">({i.kode})</span></p>
              <p className="text-xs text-muted-foreground">{i.kategori} · {i.kondisi} · {tgl(i.tanggal_beli)} · {i.metode_beli === "transfer" ? "Transfer Bank" : "Tunai"}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold">{rupiah(i.harga_beli)}</p>
              <p className={`text-[10px] font-bold uppercase tracking-wide ${i.status === "tersedia" ? "text-emerald-600" : "text-muted-foreground"}`}>{i.status}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
